import { useCallback, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  ensureRenderable,
  UnsupportedImageError,
} from "@/lib/heic/ensure-renderable";
import { ImageMarkupEditor } from "@/components/records/image-markup-editor";
import type {
  MarkupCompletePayload,
  ImageMarkupEditorHandle,
} from "@/components/records/image-markup-editor";
import { ImageViewerModal } from "@/components/common/image-viewer-modal/image-viewer-modal";
import type { ImageViewerModalProps } from "@/components/common/image-viewer-modal/types";
import { CameraCapture } from "@/components/common/camera-capture/camera-capture";
import { MarkupFilmstrip } from "@/components/common/camera-capture/markup-filmstrip";
import type { CapturedPhoto } from "./captured-photo";
import { snapshotCapturedFile } from "./snapshot-captured-file";
import { stampPhoto } from "./stamp-photo";

type CanOnResource = ImageViewerModalProps["canOnResource"];

interface UseRecordCaptureOptions {
  projectId: string;
  canOnResource: CanOnResource;
  canCreate: boolean;
  canCreateTag?: boolean;
  /** `issue:Create`. Shows the Raise-an-issue toggle on the new-record modal. */
  canCreateIssue?: boolean;
  defaultPlanId?: string | null;
  defaultPinX?: number | null;
  defaultPinY?: number | null;
  /** Plan-relative bearing in degrees `[0, 360)` for a directed ghost pin. */
  defaultPinBearing?: number | null;
  metadataFields?: ImageViewerModalProps["metadataFields"];
  projectTags?: ImageViewerModalProps["projectTags"];
  projectPlans?: ImageViewerModalProps["projectPlans"];
  /** Called when the flow fully closes (submit or final discard) */
  onFlowClosed?: () => void;
  /**
   * Optional terminal "batch handoff". When supplied, markup Done commits the
   * current photo as usual but hands the completed `CapturedPhoto[]` batch to
   * this callback INSTEAD of opening the new-record [[image-viewer-modal.spec]]
   * — letting "+ Add to an existing record" reuse the same single/multi capture
   * core ([[record-capture.spec]], [[append-photo-outbox]]). When omitted the
   * create-FAB terminal (opening the new-record modal) is unchanged.
   */
  onBatchReady?: (batch: CapturedPhoto[]) => void;
}

// The camera path drives a CapturedPhoto[] batch through its own steps; the
// library path keeps the original single-file markup → modal pipeline.
type FlowStep = "idle" | "camera" | "markup" | "modal";

export function useRecordCapture({
  projectId,
  canOnResource,
  canCreate,
  canCreateTag = false,
  canCreateIssue = false,
  defaultPlanId,
  defaultPinX,
  defaultPinY,
  defaultPinBearing,
  metadataFields = [],
  projectTags = [],
  projectPlans = [],
  onFlowClosed,
  onBatchReady,
}: UseRecordCaptureOptions) {
  // Library pick still routes through a hidden file input (camera capture is
  // now the custom getUserMedia overlay, not a native <input capture>). The
  // input carries an explicit MIME allowlist and NO `capture` attribute so the
  // OS shows the photo library / file picker.
  const libraryInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<FlowStep>("idle");

  // ---- the working batch (camera) ----
  const [batch, setBatch] = useState<CapturedPhoto[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  // Capture mode (default single): a single-mode shot goes straight to markup
  // with a fresh one-photo batch; multi-mode appends and stays on the camera.
  // Tapping the filmstrip "+" (add more) flips us to multi.
  const [mode, setMode] = useState<"single" | "multi">("single");
  // "+ Add" on a not-yet-saved draft re-enters the camera to append MORE photos
  // to the SAME working batch. Gated entirely behind this flag: when false, every
  // shared handler below behaves exactly as the create/FAB flow always has.
  const [isAddingToBatch, setIsAddingToBatch] = useState(false);
  // Batch length when "+ Add" began — a cancel mid-add truncates back to it.
  const addStartCountRef = useRef(0);
  // True while picked library files are being snapshotted + (HEIC→JPEG)
  // converted, before markup opens. Surfaces a lightweight overlay so a slow
  // conversion isn't a dead tap.
  const [isPreparingPhotos, setIsPreparingPhotos] = useState(false);

  // Imperative handle on the markup editor — lets us flush the current photo's
  // in-progress edits before the filmstrip switches photos (commit-on-switch).
  const editorRef = useRef<ImageMarkupEditorHandle>(null);

  // Annotation payload from the markup editor — forwarded to the modal as the
  // single-photo back-compat props (the modal's multi-photo grid is a separate
  // PR; here we hand it the FIRST photo so single-photo create works today).
  const [markupPayload, setMarkupPayload] = useState<MarkupCompletePayload | null>(null);
  const [takenAtHint, setTakenAtHint] = useState<string | null>(null);
  // Object URL is state (needed in render). `prevObjectUrlRef` is the SINGLE
  // source of truth for revoking it — the two are always set/cleared in
  // lockstep (see handleMarkupComplete / resetFlow), and `revokeCurrentUrl` is
  // the one place that calls `URL.revokeObjectURL` on it. The state copy exists
  // only so the modal preview can render; it is never revoked directly (that
  // would double-revoke the same URL).
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const prevObjectUrlRef = useRef<string | null>(null);

  const revokeCurrentUrl = useCallback(() => {
    if (prevObjectUrlRef.current) {
      URL.revokeObjectURL(prevObjectUrlRef.current);
      prevObjectUrlRef.current = null;
    }
  }, []);

  const revokeBatch = useCallback((photos: CapturedPhoto[]) => {
    for (const p of photos) {
      if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
    }
  }, []);

  const resetFlow = useCallback(() => {
    // Revoke object URLs here (the event handler), not inside setState updaters
    // — React may run updaters twice in dev (StrictMode), and side effects in
    // an updater must be avoided. `batch` comes from the closure; resetFlow is
    // recreated when it changes (only consumed by callback props, so the churn
    // is free). The modal preview URL is revoked exactly once via
    // `revokeCurrentUrl` (its single source of truth, `prevObjectUrlRef`).
    revokeBatch(batch);
    revokeCurrentUrl();

    setStep("idle");
    setMarkupPayload(null);
    setTakenAtHint(null);
    setBatch([]);
    setCurrentIndex(0);
    setMode("single");
    setObjectUrl(null);
    onFlowClosed?.();
  }, [batch, revokeCurrentUrl, revokeBatch, onFlowClosed]);

  // ---- camera entry ----
  const openCamera = useCallback(() => {
    setStep("camera");
  }, []);

  const openLibrary = useCallback(() => {
    libraryInputRef.current?.click();
  }, []);

  // A shutter press behaves per the capture mode:
  //  - single: the common case — start a FRESH one-photo batch and go straight
  //    to markup. Any previously-captured batch previews are revoked first so
  //    switching single → shutter never leaks object URLs.
  //  - multi: append the photo to the working batch and stay on the camera for
  //    the next shot (the WhatsApp-style stack + "Mark up" build up).
  // Side effects (revokes) live in the event handler, never in a setState
  // updater — React may re-run updaters in StrictMode dev.
  const handleCapture = useCallback(
    (photo: CapturedPhoto) => {
      if (isAddingToBatch) {
        // Adding to an existing draft: ALWAYS append (never the single-mode replace),
        // so toggling single mid-add can't wipe the originals. Single jumps to markup;
        // multi stays on the camera for a burst.
        const newIndex = batch.length;
        setBatch((prev) => [...prev, photo]);
        if (mode === "single") {
          setCurrentIndex(newIndex);
          setStep("markup");
        }
        return;
      }
      if (mode === "single") {
        revokeBatch(batch);
        setBatch([photo]);
        setCurrentIndex(0);
        setStep("markup");
        return;
      }
      setBatch((prev) => [...prev, photo]);
    },
    [mode, batch, revokeBatch, isAddingToBatch],
  );

  // Tapping the stack / "Mark up" enters markup at the newest photo.
  const handleReview = useCallback(() => {
    setCurrentIndex(Math.max(0, batch.length - 1));
    setStep("markup");
  }, [batch.length]);

  // Tapping the filmstrip "+" tile returns to the camera for another shot.
  // Adding more photos means the user is batching, so flip to multi mode — the
  // next shutter press appends to the batch rather than restarting it.
  const handleAddMore = useCallback(() => {
    setMode("multi");
    setStep("camera");
  }, []);

  // Library pick: snapshot every selected file into a CapturedPhoto batch, then
  // markup. One file → a one-photo batch (live v1.2 on submit); several files →
  // a multi-photo batch (v2.0 append on submit). The PICK COUNT — not the camera
  // mode — decides the write path; from markup on, a gallery batch is driven
  // exactly like a camera batch (filmstrip + modal handoff). See
  // [[burst-camera.spec]] US-009 / [[record-capture.spec]] US-006.
  const handleLibraryFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      e.target.value = "";
      if (files.length === 0) return;
      setIsPreparingPhotos(true);
      // Snapshot the RAW bytes first (keeps `originalFile` = the untouched
      // upload, EXIF intact — may be HEIC), THEN derive a browser-renderable
      // JPEG base via `ensureRenderable` (HEIC→JPEG; identity otherwise) for the
      // editor + preview. Snapshot before minting preview URLs so a rejection
      // partway through never leaks object URLs for photos already resolved.
      void Promise.all(
        files.map(async (file) => {
          const original = await snapshotCapturedFile(file);
          const renderFile = await ensureRenderable(original);
          // Stamp every picked photo up front. The markup editor only stamps
          // the ONE photo the user opens, so without this a multi-photo pick
          // uploads photo 1 stamped and the rest bare — the burn-in is
          // canonical for the record ([[image-markup-editor.spec]]).
          const annotatedBlob = await stampPhoto(renderFile);
          return { original, renderFile, annotatedBlob };
        }),
      )
        .then((prepared) => {
          const photos: CapturedPhoto[] = prepared
            .map(({ original, renderFile, annotatedBlob }, i) => ({
              id: crypto.randomUUID(),
              originalFile: original,
              renderFile,
              annotatedBlob,
              drawings: [],
              previewUrl: URL.createObjectURL(annotatedBlob),
              takenAtHint: new Date(files[i].lastModified).toISOString(),
            }))
            // Earliest first → the first photo becomes the record's cover (images[0]).
            .sort((a, b) =>
              (a.takenAtHint ?? "").localeCompare(b.takenAtHint ?? ""),
            );
          // Mid-"+ Add" gallery pick: APPEND to the existing draft (never the
          // replace below) so the already-captured photos survive — mirrors the
          // camera-shutter add path in handleCapture. Gated on the flag, so the
          // normal create/FAB gallery pick is unchanged.
          if (isAddingToBatch) {
            const startIdx = batch.length;
            setBatch((prev) => [...prev, ...photos]);
            setCurrentIndex(startIdx);
            setStep("markup");
            return;
          }
          // A multi-photo pick IS a multi batch — mirror burst capture so the
          // mode stays consistent if the user later taps the filmstrip "+".
          if (photos.length > 1) setMode("multi");
          setBatch(photos);
          setCurrentIndex(0);
          setStep("markup");
        })
        .catch((error) => {
          console.error("Failed to prepare selected photos:", error);
          toast(
            error instanceof UnsupportedImageError
              ? error.message
              : "Couldn't read one of those photos — it may be an unsupported or damaged image.",
          );
          // A failed "+ Add" must cancel only the add attempt and return to the
          // still-mounted modal — NOT resetFlow, which would tear down the whole
          // in-progress record (its already-captured photos + metadata). Nothing
          // was appended on failure, so returning to the modal is all that's
          // needed (mirrors cancelAdd for the no-appended-photos case).
          if (isAddingToBatch) {
            setIsAddingToBatch(false);
            setStep("modal");
          } else {
            resetFlow();
          }
        })
        .finally(() => setIsPreparingPhotos(false));
    },
    [resetFlow, isAddingToBatch, batch],
  );

  // The photo currently open in the markup editor.
  const currentPhoto = batch[currentIndex] ?? null;

  // Markup Done: commit the current photo's markup (fresh annotated blob +
  // drawings + crop) and open the create-record modal immediately with the
  // WHOLE batch. Done no longer force-marches through every photo; the
  // filmstrip is the way to annotate multiple photos (and it persists edits on
  // switch — see `handleFilmstripSelect`).
  //
  // All object-URL side effects and sibling setState calls live here in the
  // event handler — NOT inside a setBatch updater. React may invoke an updater
  // more than once (StrictMode dev double-render), which would leak object URLs
  // and double-revoke; updaters must stay pure.
  const handleMarkupComplete = useCallback(
    (payload: MarkupCompletePayload) => {
      const idx = currentIndex;
      const target = batch[idx];
      if (!target) return;

      // Revoke the photo's stale preview before minting the new annotated one.
      if (target.previewUrl) URL.revokeObjectURL(target.previewUrl);
      const updated: CapturedPhoto = {
        ...target,
        annotatedBlob: payload.annotatedBlob,
        thumbnailBlob: payload.thumbnailBlob,
        drawings: payload.drawings,
        crop: payload.crop,
        rotation: payload.rotation,
        downscaleMaxPx: payload.downscaleMaxPx,
        previewUrl: URL.createObjectURL(payload.annotatedBlob),
      };
      const next = [...batch];
      next[idx] = updated;
      setBatch(next);

      if (isAddingToBatch) {
        // The new photo(s) are now in `next`; return to the already-open modal without
        // re-pointing its cover (next[0] is still the original cover) or a fresh open.
        setIsAddingToBatch(false);
        setMode("single");
        setStep("modal");
        return;
      }

      // Batch-handoff terminal: hand the COMMITTED LOCAL batch (`next`, not
      // component state — which `resetFlow` is about to clear) to the consumer
      // and end the flow without opening the new-record modal. The handed
      // photos carry their Blobs (annotatedBlob / originalFile), so the consumer
      // can persist them even though resetFlow revokes the preview object URLs
      // (Blobs are unaffected by revoking their URLs). onBatchReady is invoked
      // before resetFlow, so the consumer reads the batch before any teardown.
      if (onBatchReady) {
        onBatchReady(next);
        resetFlow();
        return;
      }

      // Default terminal: open the modal with the whole batch. Single-photo
      // back-compat props are built from the FIRST photo.
      const first = next[0];
      revokeCurrentUrl();
      const url = URL.createObjectURL(first.annotatedBlob);
      prevObjectUrlRef.current = url;
      setMarkupPayload({
        originalFile: first.originalFile,
        annotatedBlob: first.annotatedBlob,
        thumbnailBlob: first.thumbnailBlob ?? first.annotatedBlob,
        drawings: first.drawings,
        crop: first.crop,
        downscaleMaxPx: first.downscaleMaxPx,
      });
      setObjectUrl(url);
      setTakenAtHint(first.takenAtHint);
      setStep("modal");
    },
    [batch, currentIndex, revokeCurrentUrl, onBatchReady, resetFlow, isAddingToBatch],
  );

  // Commit-on-switch: tapping a filmstrip thumb persists the current photo's
  // in-progress edits (via the editor's imperative `commitToPayload`) BEFORE
  // loading the target photo, so flipping between photos to mark them up never
  // discards work. No-op when the tap targets the already-current photo.
  const handleFilmstripSelect = useCallback(
    async (targetIndex: number) => {
      if (targetIndex === currentIndex) return;

      const idx = currentIndex;
      const current = batch[idx];
      const payload = await editorRef.current?.commitToPayload();

      if (payload && current) {
        // Persist the flushed edits into the current batch entry, mirroring
        // handleMarkupComplete's commit (revoke old preview, mint new).
        if (current.previewUrl) URL.revokeObjectURL(current.previewUrl);
        const updated: CapturedPhoto = {
          ...current,
          annotatedBlob: payload.annotatedBlob,
          thumbnailBlob: payload.thumbnailBlob,
          drawings: payload.drawings,
          crop: payload.crop,
          // Same as Done: drawings + crop live in rotation-corrected space, so
          // without this the photo reopens unrotated with its markups misplaced.
          rotation: payload.rotation,
          downscaleMaxPx: payload.downscaleMaxPx,
          previewUrl: URL.createObjectURL(payload.annotatedBlob),
        };
        setBatch((prev) => {
          const nextBatch = [...prev];
          nextBatch[idx] = updated;
          return nextBatch;
        });
      }

      setCurrentIndex(targetIndex);
    },
    [batch, currentIndex],
  );

  // "+ Add" on a not-yet-saved draft: re-enter the camera to APPEND more photos
  // to the same working batch (mirrors the Edit-markups detour — the modal stays
  // mounted, so form state survives). Starts in multi so a single shot lands in
  // markup but the user can also burst.
  const addToBatch = useCallback(() => {
    addStartCountRef.current = batch.length;
    setIsAddingToBatch(true);
    setMode("multi");
    setStep("camera");
  }, [batch.length]);

  // Each tile's "×" removes that photo from the working batch. Removing the lone
  // photo is handled upstream (discard/close); this runs for a batch of >= 2.
  const removeFromBatch = useCallback(
    (photoId: string) => {
      const idx = batch.findIndex((p) => p.id === photoId);
      if (idx < 0) return;
      const target = batch[idx];
      if (target.previewUrl) URL.revokeObjectURL(target.previewUrl);
      const next = batch.filter((p) => p.id !== photoId);
      setBatch(next);
      // Cover removed → re-point the open modal's preview at the new cover so it
      // isn't left rendering the deleted photo (mirrors handleMarkupComplete's
      // modal-open terminal). Side effects in the handler, never in an updater.
      if (idx === 0 && next.length > 0) {
        revokeCurrentUrl();
        const url = URL.createObjectURL(next[0].annotatedBlob);
        prevObjectUrlRef.current = url;
        setObjectUrl(url);
        setMarkupPayload({
          originalFile: next[0].originalFile,
          annotatedBlob: next[0].annotatedBlob,
          thumbnailBlob: next[0].thumbnailBlob ?? next[0].annotatedBlob,
          drawings: next[0].drawings,
          crop: next[0].crop,
          downscaleMaxPx: next[0].downscaleMaxPx,
        });
        setTakenAtHint(next[0].takenAtHint);
      }
    },
    [batch, revokeCurrentUrl],
  );

  // Backing out of the "+ Add" camera (Close, or a single-shot cancel).
  const cancelAdd = useCallback(() => {
    // Truncate any photos appended during this add (revoking their previews) and
    // return to the open modal — NOT resetFlow (which tears down the whole draft).
    const removed = batch.slice(addStartCountRef.current);
    revokeBatch(removed);
    setBatch(batch.slice(0, addStartCountRef.current));
    setIsAddingToBatch(false);
    setMode("single");
    setStep("modal");
  }, [batch, revokeBatch]);

  // Closing the markup editor discards the whole flow (symmetric with the
  // modal's own discard) — UNLESS we're mid-"+ Add", where backing out only
  // truncates the appended photos and returns to the still-mounted modal.
  const handleMarkupClose = useCallback(() => {
    if (isAddingToBatch) {
      cancelAdd();
      return;
    }
    resetFlow();
  }, [resetFlow, isAddingToBatch, cancelAdd]);

  // Re-enter the markup editor from the modal — a photo tile's Edit-markups
  // pencil passes ITS index, so the editor opens on the tapped photo (the
  // filmstrip still navigates the rest of the batch). Out-of-range indices
  // clamp to the first photo defensively.
  const handleEditMarkups = useCallback(
    (index: number) => {
      setCurrentIndex(index >= 0 && index < batch.length ? index : 0);
      setStep("markup");
    },
    [batch.length],
  );

  // The modal owns its own discard confirmation; by onClose the user has
  // either submitted or confirmed discard — reset either way.
  const handleModalClose = useCallback(() => {
    resetFlow();
  }, [resetFlow]);

  const latestPreviewUrl =
    batch.length > 0 ? batch[batch.length - 1].previewUrl : null;

  // capturedPhotos handed to the modal (multi-photo grid is a separate PR).
  const capturedPhotos = useMemo(() => batch, [batch]);

  const isActive = step !== "idle";

  const captureUI = (
    <>
      {isPreparingPhotos && (
        // z-[70] sits ABOVE the camera's z-[60] fixed root so an in-camera
        // gallery pick (step stays "camera" while the HEIC converts) shows the
        // overlay on top and blocks camera controls until preparation settles —
        // otherwise the later-painted camera hides it and stays interactive.
        <div
          className="fixed inset-0 z-[70] flex flex-col items-center justify-center gap-3 bg-black/60 backdrop-blur-sm"
          role="status"
          aria-live="polite"
        >
          <Loader2 className="h-8 w-8 animate-spin text-white" />
          <p className="text-sm font-medium text-white">Preparing photo…</p>
        </div>
      )}

      <input
        ref={libraryInputRef}
        type="file"
        accept="image/jpeg, image/png, image/jpg, image/heic"
        multiple
        className="hidden"
        onChange={handleLibraryFileChange}
        aria-hidden
        tabIndex={-1}
      />

      <CameraCapture
        isOpen={step === "camera"}
        onCapture={handleCapture}
        onClose={isAddingToBatch ? cancelAdd : resetFlow}
        batchCount={batch.length}
        latestPreviewUrl={latestPreviewUrl}
        onReview={handleReview}
        mode={mode}
        onModeChange={setMode}
        onPickFromLibrary={openLibrary}
        onUseLibrary={() => {
          // Fall back to the library picker from the denied / no-camera UI.
          resetFlow();
          // Defer so the camera overlay unmounts before the picker opens.
          window.setTimeout(() => libraryInputRef.current?.click(), 0);
        }}
      />

      <ImageMarkupEditor
        ref={editorRef}
        isOpen={step === "markup"}
        // Re-key per photo so the editor remounts with the right image +
        // hydrated drawings/crop when navigating the filmstrip.
        key={currentPhoto?.id ?? "none"}
        imageFile={currentPhoto?.renderFile ?? null}
        initialDrawings={currentPhoto?.drawings}
        initialCrop={currentPhoto?.crop ?? null}
        initialRotation={currentPhoto?.rotation}
        onClose={handleMarkupClose}
        onComplete={handleMarkupComplete}
        filmstripSlot={
          batch.length > 0 ? (
            <MarkupFilmstrip
              photos={batch}
              currentIndex={currentIndex}
              onSelect={(i) => void handleFilmstripSelect(i)}
              onAddMore={handleAddMore}
            />
          ) : undefined
        }
      />

      {objectUrl && markupPayload && !onBatchReady && (
        // Keep the modal mounted during markup re-edit so the user's form
        // state (plan, pin, tags, metadata) survives the round-trip. Never
        // mounts in the batch-handoff terminal — "+ Add" consumers receive the
        // batch via onBatchReady and own their own destination (the append
        // outbox), so the new-record modal must stay out of that flow.
        <ImageViewerModal
          isOpen={step === "modal"}
          onClose={handleModalClose}
          imageSrc={objectUrl}
          imageFile={markupPayload.originalFile}
          annotatedBlob={markupPayload.annotatedBlob}
          drawings={markupPayload.drawings}
          crop={markupPayload.crop}
          capturedPhotos={capturedPhotos}
          takenAtHint={takenAtHint}
          projectId={projectId}
          metadataFields={metadataFields}
          projectTags={projectTags}
          projectPlans={projectPlans}
          initialRecord={null}
          defaultPlanId={defaultPlanId}
          defaultPinX={defaultPinX}
          defaultPinY={defaultPinY}
          defaultPinBearing={defaultPinBearing}
          canOnResource={canOnResource}
          canCreate={canCreate}
          canCreateIssue={canCreateIssue}
          canCreateTag={canCreateTag}
          onEditMarkups={handleEditMarkups}
          onAddPhotos={addToBatch}
          onRemovePhoto={removeFromBatch}
        />
      )}
    </>
  );

  return { openCamera, openLibrary, captureUI, isActive };
}
